import { Dayjs } from 'dayjs'

import { useResolvedDepartmentByActiveSpecialities } from '~/app/pages/SurgeonScheduler/shared/resolvedDepartment'
import { OptimusSchemas } from '~/clients/optimus-client'
import { useStore } from '~/store/store'

import { StaffingPlanExecution, useStaffingPlanExecutions } from '../'

export type StaffingPlanExecutionStatus = OptimusSchemas['StaffingPlanExecutionStatus']

const IGNORED_STATUSES: StaffingPlanExecutionStatus[] = ['rejected', 'discarded', 'stopped']

function getMonthYear(selectedDate: Dayjs) {
    return { year: selectedDate.year(), month: selectedDate.month() + 1 }
}

export function useStaffingPlanExecutionsForSelectedMonth(options?: { selectedDate?: Dayjs }) {
    const storeSelectedDate = useStore(state => state.appFilters.selectedDate)
    const selectedDate = options?.selectedDate ?? storeSelectedDate
    const { year, month } = getMonthYear(selectedDate)

    const executions = useStaffingPlanExecutions({
        where: { year, month },
        orderBy: ['created_at'],
    })

    return executions.filter(execution => !IGNORED_STATUSES.includes(execution.status))
}

export function useCurrentDepartmentStaffingPlanExecution(): StaffingPlanExecution | null {
    const resolvedDepartment = useResolvedDepartmentByActiveSpecialities()

    const executions = useStaffingPlanExecutionsForSelectedMonth()

    if (resolvedDepartment.status !== 'resolved') {
        return null
    }

    return executions.findLast(execution => execution.department_id === resolvedDepartment.department.id) ?? null
}

export function useIsStaffingPlanExecutionBlocking() {
    const resolvedDepartment = useResolvedDepartmentByActiveSpecialities()
    const executions = useStaffingPlanExecutionsForSelectedMonth()

    if (resolvedDepartment.status !== 'resolved') {
        return executions.some(({ status }) => status === 'started')
    }
    const currentDepartmentExecution = executions.findLast(execution => execution.department_id === resolvedDepartment.department.id)
    return currentDepartmentExecution?.status === 'started'
}
